<!--
  AdminEmailTemplates.svelte — Admin CRUD page for email templates.

  Lists all email templates with create/edit/delete functionality.
  Supports template preview, placeholder management, and type filtering.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Button,
		Input,
		Spinner,
		Toggle,
		Modal,
		Label,
		Textarea,
		Heading
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import {
		listEmailTemplates,
		createEmailTemplate,
		updateEmailTemplate,
		deleteEmailTemplate,
		previewEmailTemplate
	} from '../../AppNetwork';
	import type { EmailTemplate, EmailTemplateType, SortDirection } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { ConfirmButton, SortableTableHeadCell } from '../../components';
	import { t, t2 } from '../../i18n';

	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'gray' | 'primary';

	/** All templates loaded from the server. */
	let templates: EmailTemplate[] = $state([]);
	/** Whether loading. */
	let loading = $state(true);
	/** Error text. */
	let error = $state('');
	/** Search filter. */
	let search = $state('');
	/** Type filter. */
	let typeFilter = $state('');

	// ── Sorting ───────────────────────────────────────────────────

	/** Active server-side sort key (`''` = server default, name ascending). */
	let sortBy = $state('');
	/** Active server-side sort direction. */
	let sortDir: SortDirection = $state('asc');

	/** Switch the sort column/direction and refetch from the server. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key;
		sortDir = dir;
		loadTemplates();
	}

	// ── Modal state ───────────────────────────────────────────────

	let showModal = $state(false);
	let editing: EmailTemplate | null = $state(null);
	let saving = $state(false);

	// ── Form fields ───────────────────────────────────────────────

	let formName = $state('');
	let formSubject = $state('');
	let formHtml = $state('');
	let formText = $state('');
	let formType: EmailTemplateType = $state('standalone');
	let formDescription = $state('');
	let formIsActive = $state(true);

	// ── Preview ───────────────────────────────────────────────────

	let showPreview = $state(false);
	let previewHtml = $state('');
	let previewLoading = $state(false);
	/** Whether preview is rendered in dark mode. */
	let previewDarkMode = $state(false);
	/** Template currently being previewed (to know its type). */
	let previewingTemplate: EmailTemplate | null = $state(null);
	/** Master template name selected for inner template preview. */
	let previewMasterName = $state('');
	/** Available master template names for the dropdown. */
	let masterTemplateOptions = $derived([
		{ value: '', name: '— No master template —', icon: 'fa-solid fa-ban' },
		...templates
			.filter((t) => t.templateType === 'master')
			.map((t) => ({ value: t.templateName, name: t.templateName, icon: 'fa-solid fa-file-code' }))
	]);
	/** Mock placeholder variables for preview substitution. */
	const mockVariables: Record<string, string> = {
		FIRST_NAME: 'Jane',
		LAST_NAME: 'Doe',
		FULL_NAME: 'Jane Doe',
		EMAIL: 'jane.doe@example.com',
		USERNAME: 'janedoe',
		OTP_CODE: '849201',
		ACTIVATION_URL: 'https://example.com/activate?token=mock-token-123',
		RESET_URL: 'https://example.com/reset?token=mock-token-456',
		APP_NAME: 'AbstractID',
		WEBSITE_URL: 'https://example.com'
	};

	// ── Delete ────────────────────────────────────────────────────

	/** Execute the deletion of a template. */
	async function handleDelete(tpl: EmailTemplate): Promise<void> {
		try {
			await deleteEmailTemplate(tpl.id);
			toastSuccess(t2('Template deleted.'));
			await loadTemplates();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete template'));
		}
	}

	// ── Data loading ──────────────────────────────────────────────

	/**
	 *
	 */
	async function loadTemplates(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch all templates from the server and handle errors. */
		try {
			templates = await listEmailTemplates({
				sortBy: sortBy || undefined,
				sortDir: sortBy ? sortDir : undefined
			});
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load templates');
		} finally {
			loading = false;
		}
	}

	// ── Filtered list ─────────────────────────────────────────────

	let filtered = $derived(templates.filter((t) => {
		const matchesSearch =
			!search ||
			t.templateName.toLowerCase().includes(search.toLowerCase()) ||
			t.subject.toLowerCase().includes(search.toLowerCase());
		const matchesType = !typeFilter || t.templateType === typeFilter;
		return matchesSearch && matchesType;
	}));

	/** Template type options. */
	const typeOptions = [
		{ value: '', name: 'All Types', icon: 'fa-solid fa-list' },
		{ value: 'master', name: 'Master', icon: 'fa-solid fa-layer-group' },
		{ value: 'inner', name: 'Inner', icon: 'fa-solid fa-file-lines' },
		{ value: 'standalone', name: 'Standalone', icon: 'fa-solid fa-file' }
	];

	const formTypeOptions = [
		{ value: 'standalone', name: 'Standalone', icon: 'fa-solid fa-file' },
		{ value: 'master', name: 'Master', icon: 'fa-solid fa-layer-group' },
		{ value: 'inner', name: 'Inner', icon: 'fa-solid fa-file-lines' }
	];

	/** Badge colours per template type. */
	const typeBadgeColors: Record<string, BadgeColor> = {
		master: 'purple',
		inner: 'blue',
		standalone: 'green'
	};

	// ── Modal helpers ─────────────────────────────────────────────

	/**
	 *
	 */
	function openCreate(): void {
		editing = null;
		formName = '';
		formSubject = '';
		formHtml = '';
		formText = '';
		formType = 'standalone';
		formDescription = '';
		formIsActive = true;
		showModal = true;
	}

	/**
	 *
	 */
	function openEdit(tpl: EmailTemplate): void {
		editing = tpl;
		formName = tpl.templateName;
		formSubject = tpl.subject;
		formHtml = tpl.htmlContent;
		formText = tpl.textContent || '';
		formType = tpl.templateType;
		formDescription = tpl.description || '';
		formIsActive = tpl.isActive;
		showModal = true;
	}

	/**
	 *
	 */
	async function handleSave(): Promise<void> {
		if (!formName.trim() || !formSubject.trim()) {
			error = t2('Template name and subject are required.');
			return;
		}

		saving = true;
		error = '';

		const payload: Partial<EmailTemplate> = {
			templateName: formName.trim(),
			subject: formSubject.trim(),
			htmlContent: formHtml,
			textContent: formText || null,
			templateType: formType,
			description: formDescription || null,
			isActive: formIsActive
		};

		/** Persist the template (create or update) and refresh the list. */
		try {
			if (editing) {
				await updateEmailTemplate(editing.id, payload);
			} else {
				await createEmailTemplate(payload);
			}
			showModal = false;
			editing = null;
			toastSuccess(editing ? t2('Template updated.') : t2('Template created.'));
			await loadTemplates();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save template'));
		} finally {
			saving = false;
		}
	}

	// ── Preview ───────────────────────────────────────────────────

	/**
	 *
	 */
	async function handlePreview(tpl: EmailTemplate): Promise<void> {
		previewLoading = true;
		previewHtml = '';
		previewingTemplate = tpl;
		previewDarkMode = false;
		/* Auto-select first master template for inner templates if none selected. */
		if (tpl.templateType === 'inner' && !previewMasterName) {
			const firstMaster = templates.find((t) => t.templateType === 'master');
			if (firstMaster) previewMasterName = firstMaster.templateName;
		}
		showPreview = true;
		await refreshPreview(tpl);
	}

	/** Re-fetch the preview with current master and variables. */
	async function refreshPreview(tpl?: EmailTemplate): Promise<void> {
		const target = tpl || previewingTemplate;
		/** Bail out if no template is available to preview. */
		if (!target) return;
		previewLoading = true;
		/** Fetch the rendered preview HTML from the server. */
		try {
			const masterArg = target.templateType === 'inner' && previewMasterName ? previewMasterName : undefined;
			const result = await previewEmailTemplate(target.id, mockVariables, masterArg);
			previewHtml = result.html;
		} catch (e) {
			previewHtml = `<p style="color:red">Preview failed: ${e instanceof Error ? e.message : 'Unknown error'}</p>`;
		} finally {
			previewLoading = false;
		}
	}

	/** Build the iframe srcdoc for the preview, optionally adding dark-mode styles. */
	let previewSrcdoc = $derived(previewDarkMode
		? `<html><head><style>
			@media (prefers-color-scheme: dark) { body { background: #1a1a2e !important; color: #e0e0e0 !important; } }
			body { background: #1a1a2e !important; color: #e0e0e0 !important; }
			a { color: #6ea8fe !important; }
			table, td, th { border-color: #444 !important; }
			img { opacity: 0.9; }
		</style></head><body>${previewHtml}</body></html>`
		: previewHtml);

	// ── Delete ────────────────────────────────────────────────────

	/**
	 *
	 */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	onMount(loadTemplates);
</script>

<Heading tag="h3">{$t("Email Templates")}</Heading>

<div class="space-y-6">
	<!-- Toolbar -->
	<div class="flex flex-wrap items-center gap-4">
		<Input bind:value={search} placeholder={$t("Search templates...")} class="max-w-md flex-1">
			{#snippet left()}
						<i  class="fa-solid fa-search text-gray-400"></i>
					{/snippet}
		</Input>
		<div class="w-40">
			<AbstractDropdown items={typeOptions} bind:value={typeFilter} />
		</div>
		<Button onclick={openCreate}>
			<i class="fa-solid fa-plus me-2"></i>{$t("New Template")}
		</Button>
	</div>

	{#if error}
		<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
			<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
		</div>
	{/if}

	{#if loading}
		<div class="flex justify-center py-12">
			<Spinner size="8" />
		</div>
	{:else if filtered.length === 0}
		<div class="py-12 text-center">
			<i class="fa-solid fa-envelope mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
			<p class="text-gray-500 dark:text-gray-400">
				{search || typeFilter ? $t('No templates match your filters') : $t('No email templates yet')}
			</p>
		</div>
	{:else}
		<AbstractCard class="p-0 max-w-none">
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<SortableTableHeadCell key="name" {sortBy} {sortDir} onsort={handleSort}>
						{$t("Template")}
					</SortableTableHeadCell>
					<SortableTableHeadCell key="subject" {sortBy} {sortDir} onsort={handleSort}>
						{$t("Subject")}
					</SortableTableHeadCell>
					<SortableTableHeadCell key="type" {sortBy} {sortDir} onsort={handleSort}>{$t("Type")}</SortableTableHeadCell>
					<SortableTableHeadCell key="status" {sortBy} {sortDir} onsort={handleSort}>
						{$t("Status")}
					</SortableTableHeadCell>
					<SortableTableHeadCell key="updated" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
						{$t("Updated")}
					</SortableTableHeadCell>
					<TableHeadCell>{$t("Actions")}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each filtered as tpl (tpl.id)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">
								<div>
									<div>{tpl.templateName}</div>
									{#if tpl.description}
										<div class="max-w-[200px] truncate text-xs text-gray-500 dark:text-gray-400">
											{tpl.description}
										</div>
									{/if}
								</div>
							</TableBodyCell>
							<TableBodyCell>
								<span class="block max-w-[180px] truncate text-sm text-gray-600 dark:text-gray-300"
									>{tpl.subject}</span
								>
							</TableBodyCell>
							<TableBodyCell>
								<Badge color={typeBadgeColors[tpl.templateType] || 'gray'}>{tpl.templateType}</Badge>
							</TableBodyCell>
							<TableBodyCell>
								{#if tpl.isActive}
									<Badge color="green">{$t("Active")}</Badge>
								{:else}
									<Badge color="red">{$t("Inactive")}</Badge>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">
								{formatDate(tpl.updated)}
							</TableBodyCell>
							<TableBodyCell>
								<div class="flex gap-1">
									<Button size="xs" color="alternative" onclick={() => openEdit(tpl)} title={$t("Edit")}>
										<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
									</Button>
									<Button
										size="xs"
										color="alternative"
										onclick={() => handlePreview(tpl)}
										title={$t("Preview")}
									>
										<i class="fa-solid fa-eye text-xs"></i>
									</Button>
									<ConfirmButton
										size="xs"
										color="red"
										outline
										action={$t("delete this template")}
										onconfirm={() => handleDelete(tpl)}
									>
										<i class="fa-solid fa-trash text-xs"></i>
									</ConfirmButton>
								</div>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		</AbstractCard>
	{/if}
</div>

<!-- ═══ Create/Edit Modal ═══ -->
<Modal bind:open={showModal} title={editing ? t2('Edit Template') : t2('New Template')} size="xl">
	<div class="space-y-4">
		<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
			<div>
				<Label class="mb-1">{$t("Template Name")} *</Label>
				<Input bind:value={formName} placeholder="e.g. welcome-email" />
			</div>
			<div>
				<Label class="mb-1">{$t("Type")}</Label>
				<AbstractDropdown items={formTypeOptions} value={formType} onchange={(v) => { formType = v as EmailTemplateType; }} />
			</div>
		</div>

		<div>
			<Label class="mb-1">{$t("Subject")} *</Label>
			<Input bind:value={formSubject} placeholder={$t("Email subject line...")} />
		</div>

		<div>
			<Label class="mb-1">{$t("Description")}</Label>
			<Input bind:value={formDescription} placeholder={$t("Brief description...")} />
		</div>

		<div>
			<Label class="mb-1">{$t("HTML Content")}</Label>
			<Textarea bind:value={formHtml} rows={12} class="w-full font-mono text-sm" placeholder="<html>...</html>" />
		</div>

		<!-- Live preview of the HTML content while editing -->
		{#if formHtml}
			<div>
				<Label class="mb-1">{$t("Live Preview")}</Label>
				<div
					class="bg-depth overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
					style="min-height: 200px;"
				>
					<iframe
						title={$t("Template Preview")}
						srcdoc={formHtml}
						class="w-full border-0"
						style="min-height: 200px; pointer-events: none;"
						sandbox=""
					></iframe>
				</div>
			</div>
		{/if}

		<div>
			<Label class="mb-1">{$t("Text Content (fallback)")}</Label>
			<Textarea bind:value={formText} rows={4} class="w-full font-mono text-sm" placeholder={$t("Plain text version...")} />
		</div>

		<div class="flex gap-6 border-t border-gray-200 pt-2 dark:border-gray-700">
			<Toggle bind:checked={formIsActive}>{$t("Active")}</Toggle>
		</div>

		<div class="rounded-lg bg-blue-50 p-3 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
			<i class="fa-solid fa-circle-info me-1"></i>
			<strong>{$t("Available placeholders:")}</strong>
			<code class="mx-1">{'{{FIRST_NAME}}'}</code>
			<code class="mx-1">{'{{LAST_NAME}}'}</code>
			<code class="mx-1">{'{{FULL_NAME}}'}</code>
			<code class="mx-1">{'{{EMAIL}}'}</code>
			<code class="mx-1">{'{{USERNAME}}'}</code>
			<code class="mx-1">{'{{ACTIVATION_URL}}'}</code>
			<code class="mx-1">{'{{RESET_URL}}'}</code>
			<code class="mx-1">{'{{OTP_CODE}}'}</code>
			{#if formType === 'master'}<code class="mx-1">{'{{BODY}}'}</code>{/if}
		</div>
	</div>

	{#snippet footer()}
	
			<div class="flex gap-2">
				<Button color="primary" onclick={handleSave} disabled={saving}>
					{#if saving}<Spinner size="4" class="mr-2" />{/if}
					{editing ? $t('Save Changes') : $t('Create Template')}
				</Button>
				<Button color="alternative" onclick={() => (showModal = false)}>{$t("Cancel")}</Button>
			</div>
		
	{/snippet}
</Modal>

<!-- ═══ Preview Modal ═══ -->
<Modal bind:open={showPreview} title={$t("Template Preview")} size="xl">
	<!-- Preview toolbar -->
	<div class="mb-4 flex flex-wrap items-center gap-3">
		{#if previewingTemplate?.templateType === 'inner'}
			<div class="w-56">
				<Label class="mb-1 text-xs">{$t("Master Template")}</Label>
				<AbstractDropdown items={masterTemplateOptions} bind:value={previewMasterName} onchange={() => refreshPreview()} size="sm" />
			</div>
		{/if}
		<div class="ml-auto flex items-center gap-2">
			<Toggle bind:checked={previewDarkMode} size="small">
				<i class="fa-solid {previewDarkMode ? 'fa-moon' : 'fa-sun'} me-1"></i>
				{previewDarkMode ? $t('Dark') : $t('Light')}
			</Toggle>
			<Button size="xs" color="alternative" onclick={() => refreshPreview()}>
				<i class="fa-solid fa-refresh me-1"></i>{$t("Refresh")}
			</Button>
		</div>
	</div>

	{#if previewLoading}
		<div class="flex justify-center py-8"><Spinner size="8" /></div>
	{:else}
		<div
			class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 {previewDarkMode
				? 'bg-gray-900'
				: 'bg-white'}"
		>
			<iframe
				title={$t("Email Template Preview")}
				srcdoc={previewSrcdoc}
				class="w-full border-0"
				style="min-height: 500px;"
				sandbox=""
			></iframe>
		</div>
	{/if}
</Modal>

<!-- Delete confirmation is now handled inline by ConfirmButton -->
